/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module logic/reports/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { getToken, refreshToken, signOut } from "../../utils/common.js";
import { getActiveEvents } from "../events";
import { getActiveBusinessEvents } from "../businessEvents";
import { getActiveB2BEvents } from "../b2bEvents";
import { getUser } from "../user";
import {
  canViewPastEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import {
  assertAuthorizedApiResponse,
  getAuthJsonHeaders,
  parseExportDownloadUrl,
} from "../../utils/eventApi.js";

export const obtainBuyerAssignmentsReport = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/event/${eventId}/buyerSpaceAssignments`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await obtainBuyerAssignmentsReport(eventId, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    if (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED") {
      throw error;
    }
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

export const exportAuthenticationLogs = async (
  companyId,
  startDate,
  endDate,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ companyId, startDate, endDate }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/authenticationLogsExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportAuthenticationLogs(
        companyId,
        startDate,
        endDate,
        navigate,
      );
    }

    await assertAuthorizedApiResponse(response, data, navigate);
    if (!response.ok) {
      const err = new Error(
        data?.message || "Error exporting authentication logs",
      );
      if (data?.code != null) {
        err.code = data.code;
      }
      throw err;
    }
    return parseExportDownloadUrl(data);
  } catch (error) {
    if (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED") {
      throw error;
    }
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

export const exportBuyerLogs = async (eventId, buyerInformation, navigate) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ eventId, buyerInformation }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/buyerDeactivationLogsExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportBuyerLogs(eventId, buyerInformation, navigate);
    }

    await assertAuthorizedApiResponse(response, data, navigate);
    if (!response.ok) {
      const err = new Error(data?.message || "Error exporting buyer logs");
      if (data?.code != null) {
        err.code = data.code;
      }
      throw err;
    }
    return parseExportDownloadUrl(data);
  } catch (error) {
    if (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED") {
      throw error;
    }
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

export const exportB2bEventAttendanceLogs = async (
  eventId,
  buyerInformation,
  buyerUserId,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ eventId, buyerInformation, buyerUserId }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/b2bEventAttendancesExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportB2bEventAttendanceLogs(
        eventId,
        buyerInformation,
        buyerUserId,
        navigate,
      );
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    } else {
      if (data.code === "NO_APPOINTMENTS") {
        const err = new Error(data.message);
        err.code = data.code;
        throw err;
      } else {
        const err = new Error("Error exporting b2b event attendance logs");
        if (data.code != null) {
          err.code = data.code;
        }
        throw err;
      }
    }
  } catch (error) {
    throw error;
  }
};

export const exportInvoiceLogs = async (
  year,
  eventId,
  billed,
  type,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ year, eventId, billed, type }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/invoicesExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportInvoiceLogs(year, eventId, billed, type, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    } else {
      if (data.code === "NO_INVOICES") {
        const err = new Error(data.message);
        err.code = data.code;
        throw err;
      } else {
        const err = new Error("Error exporting invoice logs");
        if (data.code != null) {
          err.code = data.code;
        }
        throw err;
      }
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches and combines active events, business events, b2b events.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setEventsList - State setter for events list
 * @returns {Promise<void>}
 */
/**
 * Fetches and combines active events for Reports selectors.
 * Eligible users also receive past non-cancelled events (futures first).
 * @async
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setEventsList - State setter for events list
 * @param {object|null|undefined} [permissions]
 * @returns {Promise<void>}
 */
export const getActiveEventsData = async (
  navigate,
  setEventsList,
  permissions,
) => {
  const userId = localStorage.getItem("userId");
  const user = userId ? await getUser(userId, navigate) : null;
  const includePast = canViewPastEvents(user, permissions);

  const events = await getActiveEvents(navigate, { includePast, userId });
  const businessEvents = await getActiveBusinessEvents(navigate);
  const b2bEvents = await getActiveB2BEvents(navigate, { includePast });

  const activeEvents = sortEventsFuturesFirst([
    ...(events || []),
    ...(businessEvents || []),
    ...(b2bEvents || []),
  ]);

  setEventsList(activeEvents);
};

/**
 * Exports a detailed report of business event appointments.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<string|null>} S3 download link for the generated report
 * @throws {Error} If the export fails
 */
export const exportBusinessEventAppointmentDetailsReport = async (
  eventId,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/event/${eventId}/appointmentDetails`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await exportBusinessEventAppointmentDetailsReport(
        eventId,
        navigate,
      );
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    }
    if (data.code === "NO_APPOINTMENTS") {
      const err = new Error(data.message);
      err.code = data.code;
      throw err;
    }
    const err = new Error(
      "Error exporting business event appointment details report",
    );
    if (data.code != null) {
      err.code = data.code;
    }
    throw err;
  } catch (error) {
    throw error;
  }
};

/**
 * Exports a general report on business events buyer companies appointments.
 * @async
 * @param {string} eventId - Event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 * @throws {Error} If the export fails
 */
export const exportGeneralReportOnBusinessEventsBuyerCompaniesAppointments =
  async (eventId, navigate) => {
    const options = {
      method: "POST",
      headers: getAuthJsonHeaders(),
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}report/event/${eventId}/buyerCompanySummary`,
        options,
      );
      const data = await response.json();
      if (data?.message === TOKEN_EXPIRED_MESSAGE) {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return [];
        }
        return await exportGeneralReportOnBusinessEventsBuyerCompaniesAppointments(
          eventId,
          navigate,
        );
      }
      await assertAuthorizedApiResponse(response, data, navigate);
      if (response.ok) {
        return parseExportDownloadUrl(data);
      } else {
        if (data.code === "NO_COMPANIES_DATA") {
          const err = new Error(data.message);
          err.code = data.code;
          throw err;
        } else {
          const err = new Error(
            "Error exporting general report on business events buyer companies appointments",
          );
          if (data.code != null) {
            err.code = data.code;
          }
          throw err;
        }
      }
    } catch (error) {
      throw error;
    }
  };

/**
 * Exports a business event requirements report.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<string|null>} S3 download link for the generated report
 * @throws {Error} If the export fails
 */
export const exportBusinessEventRequirementsReport = async (
  eventId,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/event/${eventId}/businessEventRequirements`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await exportBusinessEventRequirementsReport(eventId, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    }
    if (data.code === "NO_REQUIREMENTS") {
      const err = new Error(data.message);
      err.code = data.code;
      throw err;
    }
    const err = new Error("Error exporting business event requirements report");
    if (data.code != null) {
      err.code = data.code;
    }
    throw err;
  } catch (error) {
    throw error;
  }
};

/**
 * Exports a accumulated events connections report.
 * @async
 * @param {string} year - Year
 * @param {string} eventTypes - Event types
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 * @throws {Error} If the export fails
 */
export const exportAccumulatedEventsConnectionsReport = async (
  year,
  eventTypes,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ year, eventTypes }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/vinculacionesAcumuladasExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportAccumulatedEventsConnectionsReport(
        year,
        eventTypes,
        navigate,
      );
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    } else {
      if (data.code === "NO_EVENTS_DATA") {
        const err = new Error(data.message);
        err.code = data.code;
        throw err;
      } else {
        const err = new Error(
          "Error exporting accumulated events connections report",
        );
        if (data.code != null) {
          err.code = data.code;
        }
        throw err;
      }
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Exports a events attendances report.
 * @async
 * @param {string} year - Year
 * @param {string} eventTypes - Event types
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 * @throws {Error} If the export fails
 */
export const exportEventsAttendancesReport = async (
  year,
  eventTypes,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ year, eventTypes }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/eventsAttendancesExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportEventsAttendancesReport(year, eventTypes, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    } else {
      if (data.code === "NO_EVENTS_DATA") {
        const err = new Error(data.message);
        err.code = data.code;
        throw err;
      } else {
        const err = new Error("Error exporting events attendances report");
        if (data.code != null) {
          err.code = data.code;
        }
        throw err;
      }
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Exports a members companies quantities report.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 * @throws {Error} If the export fails
 */
/**
 * Exports a general B2B event report with successful appointments.
 * @async
 * @param {string} eventId - B2B event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<string|null>} S3 download link for the generated report
 * @throws {Error} If the export fails
 */
export const exportGeneralB2BReport = async (eventId, navigate) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/b2bEvent/${eventId}/generalReport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await exportGeneralB2BReport(eventId, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    }
    if (data.code === "NO_APPOINTMENTS") {
      const err = new Error(data.message);
      err.code = data.code;
      throw err;
    }
    const err = new Error("Error exporting general B2B report");
    if (data.code != null) {
      err.code = data.code;
    }
    throw err;
  } catch (error) {
    throw error;
  }
};

export const exportSACReport = async (navigate) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/memberCompaniesStatisticsExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await exportSACReport(navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    } else {
      if (data.code === "NO_COMPANIES") {
        const err = new Error(data.message);
        err.code = data.code;
        throw err;
      } else {
        const err = new Error(
          "Error exporting members companies quantities report",
        );
        if (data.code != null) {
          err.code = data.code;
        }
        throw err;
      }
    }
  } catch (error) {
    throw error;
  }
};

export const exportVirtualEventsRegistrationReport = async (
  eventId,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}report/event/${eventId}/virtualEventAttendancesExport`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await exportVirtualEventsRegistrationReport(eventId, navigate);
    }
    await assertAuthorizedApiResponse(response, data, navigate);
    if (response.ok) {
      return parseExportDownloadUrl(data);
    }
    if (data.code === "NO_ATTENDANCES") {
      const err = new Error(data.message);
      err.code = data.code;
      throw err;
    }
    const err = new Error("Error exporting general B2B report");
    if (data.code != null) {
      err.code = data.code;
    }
    throw err;
  } catch (error) {
    throw error;
  }
};
